export type AgentType =
  | 'Content Creator'
  | 'Marketing Manager'
  | 'Financial Advisor'
  | 'HR Manager'
  | 'Data Analyst'
  | 'Customer Manager'
  | 'Legal Assistant'
  | 'World Monitor'
  | 'IT Support Agent'
  | 'Co CEO'
  | 'Internal Trainer'
  | 'Copilot'
  | 'Performance Tracker';

export interface AgentChannel {
  id: string;
  name: string;
  type: 'internal' | 'external';
  platform: 'email' | 'slack' | 'teams' | 'whatsapp' | 'phone' | 'web' | 'api' | 'sms';
  enabled: boolean;
}

export interface AgentConfig {
  model: string;
  prompt: string;
  tools: string[];
  reportingSchedule: {
    frequency: 'real-time' | 'hourly' | 'daily' | 'weekly' | 'monthly';
    reportsTo: string[]; // Agent IDs or user IDs
    priority: 'low' | 'medium' | 'high' | 'critical';
  };
  theme: string;
  profilePicture: string;
  customName: string;
}

export interface AgentOutput {
  id: string;
  name: string;
  format: 'text' | 'image' | 'video' | 'audio' | 'document' | 'data' | 'report';
  description: string;
  frequency: string;
}

/**
 * Custom tab descriptor preserved end-to-end from the backend `ResolvedAgent`.
 * `app_id` drives capability-driven filtering on the console (Task 075 §C2,
 * V2.1 architecture). For a tab without an owning module the field is null.
 */
export interface AgentTabDescriptor {
  id: string;
  label: string;
  app_id?: string | null;
}

export interface Agent {
  id: string;
  name: string;
  customName?: string; // User-configurable display name
  avatar: string;
  type: AgentType;
  themeColor: string;
  icon: string; // Icon representing the agent's function
  description?: string; // Short description for display
  slug: string; // URL-friendly identifier

  // New comprehensive attributes
  requirements: string[]; // What the agent needs to work optimally
  outputs: AgentOutput[]; // Content and format of deliverables
  dependencies: string[]; // Agent IDs this agent depends on
  frontendComponents: string[]; // Frontend components needed for display
  channels: AgentChannel[]; // Communication channels
  config: AgentConfig; // Model, prompt, tools, reporting config
  tabs?: string[]; // DEPRECATED — kept for offline VITE_USE_AGENT_API=false fallback
  customTabs?: AgentTabDescriptor[]; // Backend-driven tabs (Task 075 §C2)
}

export interface GlobalNavItem {
  id: string;
  label: string;
  icon: string; // Icon name for the Icon component
  path: string;
  emoji?: string;
}

// Navigation items with translation keys
export const globalNavigation: GlobalNavItem[] = [
  { id: 'home', label: 'common.home', icon: 'home', path: '/home', emoji: '🏠' },
  { id: 'dashboard', label: 'common.dashboard', icon: 'barChart2', path: '/dashboard', emoji: '📊' },
  { id: 'inbox', label: 'common.inbox', icon: 'mail', path: '/inbox', emoji: '📥' },
  { id: 'knowledge', label: 'common.knowledge', icon: 'bookOpen', path: '/knowledge', emoji: '📚' },
  { id: 'apps', label: 'common.apps', icon: 'grid', path: '/apps', emoji: '🧰' },
  { id: 'settings', label: 'common.settings', icon: 'tool', path: '/settings', emoji: '⚙️' },
];

export const agents: Agent[] = [
  {
    id: 'alex-creator',
    name: 'agents.types.contentCreator',
    customName: 'Alex Creator',
    avatar: '/agent_profile_pictures/alex-creator.webp', 
    type: 'Content Creator', 
    themeColor: '#F59E0B',
    icon: 'edit',
    description: 'agents.descriptions.contentCreator',
    slug: 'alex-creator',
    requirements: ['Generative AI access (fal.ai, replicate, wavespeed)', 'Design templates', 'Brand guidelines', 'Content calendar'],
    outputs: [
      { id: 'social-posts', name: 'Social Media Posts', format: 'image', description: 'Visual content for social platforms', frequency: 'Daily' },
      { id: 'video-content', name: 'Video Assets', format: 'video', description: 'Short-form video content', frequency: 'Weekly' },
      { id: 'blog-articles', name: 'Blog Articles', format: 'document', description: 'Written content pieces', frequency: 'Weekly' }
    ],
    dependencies: ['maya-marketing'], // Marketing Manager for strategy
    frontendComponents: ['MediaGallery', 'ContentCalendar', 'TemplateLibrary'],
    channels: [
      { id: 'design-tools', name: 'Design Tools API', type: 'external', platform: 'api', enabled: true },
      { id: 'social-platforms', name: 'Social Media APIs', type: 'external', platform: 'api', enabled: true }
    ],
    config: {
      model: 'claude-3-sonnet',
      prompt: 'You are a creative content creator specialized in generating engaging visual and written content...',
      tools: ['image-generation', 'video-editing', 'template-engine'],
      reportingSchedule: { frequency: 'daily', reportsTo: ['maya-marketing'], priority: 'medium' },
      theme: '#F59E0B',
      profilePicture: '/agent_profile_pictures/alex-creator.webp',
      customName: 'Alex Creator'
    }
  },
  {
    id: 'maya-marketing',
    name: 'agents.types.marketingManager',
    customName: 'Maya Marketing',
    avatar: '/agent_profile_pictures/maya-marketing.webp', 
    type: 'Marketing Manager', 
    themeColor: '#E11D48',
    icon: 'trendingUp',
    description: 'agents.descriptions.marketingManager',
    slug: 'maya-marketing',
    requirements: ['Social media platform APIs', 'Analytics tools', 'Competitor monitoring tools', 'A/B testing platform'],
    outputs: [
      { id: 'performance-reports', name: 'Performance Reports', format: 'report', description: 'Social media analytics and insights', frequency: 'Weekly' },
      { id: 'campaign-plans', name: 'Campaign Plans', format: 'document', description: 'Marketing campaign strategies', frequency: 'Monthly' },
      { id: 'competitor-analysis', name: 'Competitor Analysis', format: 'report', description: 'Market intelligence reports', frequency: 'Weekly' }
    ],
    dependencies: ['alex-creator', 'ian-support'], // Content Creator, IT Support
    frontendComponents: ['AnalyticsDashboard', 'CampaignManager', 'CompetitorTracker'],
    channels: [
      { id: 'social-apis', name: 'Social Media APIs', type: 'external', platform: 'api', enabled: true },
      { id: 'analytics-tools', name: 'Analytics APIs', type: 'external', platform: 'api', enabled: true }
    ],
    config: {
      model: 'gpt-4-turbo',
      prompt: 'You are a marketing manager expert in social media strategy, campaign optimization, and competitive analysis...',
      tools: ['social-analytics', 'competitor-monitoring', 'ab-testing'],
      reportingSchedule: { frequency: 'weekly', reportsTo: ['tracy-trainer'], priority: 'high' },
      theme: '#E11D48',
      profilePicture: '/agent_profile_pictures/maya-marketing.webp',
      customName: 'Maya Marketing'
    }
  },
  {
    id: 'felix-finance',
    name: 'agents.types.financialAdvisor',
    customName: 'Felix Finance',
    avatar: '/agent_profile_pictures/felix-finance.webp', 
    type: 'Financial Advisor', 
    themeColor: '#059669',
    icon: 'pieChart',
    description: 'agents.descriptions.financialAdvisor',
    slug: 'felix-finance',
    requirements: ['Accounting software integration', 'Banking APIs', 'Financial data feeds', 'Reporting templates'],
    outputs: [
      { id: 'financial-reports', name: 'Financial Reports', format: 'report', description: 'Monthly and quarterly financial summaries', frequency: 'Monthly' },
      { id: 'forecasts', name: 'Financial Forecasts', format: 'document', description: 'Budget and revenue projections', frequency: 'Quarterly' },
      { id: 'expense-analysis', name: 'Expense Analysis', format: 'data', description: 'Cost optimization recommendations', frequency: 'Weekly' }
    ],
    dependencies: ['dana-data'], // Data Analyst
    frontendComponents: ['FinancialDashboard', 'BudgetTracker', 'ForecastCharts'],
    channels: [
      { id: 'accounting-apis', name: 'Accounting APIs', type: 'external', platform: 'api', enabled: true },
      { id: 'banking-apis', name: 'Banking APIs', type: 'external', platform: 'api', enabled: true }
    ],
    config: {
      model: 'claude-3-opus',
      prompt: 'You are a financial advisor expert in business finance, budgeting, forecasting, and financial analysis...',
      tools: ['financial-modeling', 'data-analysis', 'reporting'],
      reportingSchedule: { frequency: 'weekly', reportsTo: ['tracy-trainer'], priority: 'high' },
      theme: '#059669',
      profilePicture: '/agent_profile_pictures/felix-finance.webp',
      customName: 'Felix Finance'
    }
  },
  {
    id: 'harper-hr',
    name: 'agents.types.hrManager',
    customName: 'Harper HR',
    avatar: '/agent_profile_pictures/harper-hr.webp', 
    type: 'HR Manager', 
    themeColor: '#7C3AED',
    icon: 'users',
    description: 'agents.descriptions.hrManager',
    slug: 'harper-hr',
    requirements: ['LinkedIn API', 'Indeed API', 'ATS integration', 'Document templates', 'Contract templates'],
    outputs: [
      { id: 'candidate-reports', name: 'Candidate Reports', format: 'report', description: 'Recruitment pipeline and candidate analysis', frequency: 'Weekly' },
      { id: 'job-postings', name: 'Job Postings', format: 'document', description: 'Optimized job descriptions', frequency: 'As needed' },
      { id: 'contracts', name: 'Employment Contracts', format: 'document', description: 'Legal employment documents', frequency: 'As needed' }
    ],
    dependencies: ['lex-legal'], // Legal Assistant for contract review
    frontendComponents: ['CandidateTracker', 'InterviewScheduler', 'RecruitmentPipeline'],
    channels: [
      { id: 'linkedin-api', name: 'LinkedIn API', type: 'external', platform: 'api', enabled: true },
      { id: 'job-boards', name: 'Job Board APIs', type: 'external', platform: 'api', enabled: true },
      { id: 'email', name: 'Email', type: 'external', platform: 'email', enabled: true }
    ],
    config: {
      model: 'gpt-4-turbo',
      prompt: 'You are an HR manager expert in talent acquisition, recruitment, and employee management...',
      tools: ['resume-parser', 'job-matching', 'interview-scheduler'],
      reportingSchedule: { frequency: 'weekly', reportsTo: ['tracy-trainer'], priority: 'medium' },
      theme: '#7C3AED',
      profilePicture: '/agent_profile_pictures/harper-hr.webp',
      customName: 'Harper HR'
    }
  },
  {
    id: 'dana-data',
    name: 'agents.types.dataAnalyst',
    customName: 'Dana Data',
    avatar: '/agent_profile_pictures/dana-data.webp', 
    type: 'Data Analyst', 
    themeColor: '#0EA5E9',
    icon: 'database',
    description: 'agents.descriptions.dataAnalyst',
    slug: 'dana-data',
    requirements: ['Database access', 'Python environment', 'Excel/Google Sheets API', 'MCP servers', 'Data visualization tools'],
    outputs: [
      { id: 'data-reports', name: 'Data Analysis Reports', format: 'report', description: 'Statistical analysis and insights', frequency: 'Weekly' },
      { id: 'dashboards', name: 'Interactive Dashboards', format: 'data', description: 'Real-time data visualizations', frequency: 'Daily' },
      { id: 'python-scripts', name: 'Python Scripts', format: 'document', description: 'Automated data processing scripts', frequency: 'As needed' }
    ],
    dependencies: [], // Independent data source
    frontendComponents: ['DataVisualization', 'QueryBuilder', 'ScriptEditor'],
    channels: [
      { id: 'database-apis', name: 'Database APIs', type: 'internal', platform: 'api', enabled: true },
      { id: 'sheets-api', name: 'Sheets API', type: 'external', platform: 'api', enabled: true }
    ],
    config: {
      model: 'claude-3-sonnet',
      prompt: 'You are a data analyst expert in statistical analysis, data visualization, and business intelligence...',
      tools: ['python-execution', 'sql-query', 'data-visualization'],
      reportingSchedule: { frequency: 'daily', reportsTo: ['tracy-trainer', 'felix-finance'], priority: 'high' },
      theme: '#0EA5E9',
      profilePicture: '/agent_profile_pictures/dana-data.webp',
      customName: 'Dana Data'
    }
  },
  // Chris Customer is intentionally absent from this offline catalog.
  // Task 100 F01 — its hardcoded attributes (themeColor '#EC4899' pink vs
  // backend cyan '#06B6D4', model 'gpt-4-turbo' which the FOCAL brain does
  // NOT impose) had drifted from the backend AgentTemplate seed and lied
  // to anyone reading this file. Chris is the only production vertical and
  // is always resolved from the backend (VITE_USE_AGENT_API defaults on);
  // this dev-only fallback never serves it. See
  // docs/tasks/active/098_chris_customer_behavior_audit/findings/F01_template_frontend_backend_drift.md
  {
    id: 'lex-legal',
    name: 'agents.types.legalAssistant',
    customName: 'Lex Legal',
    avatar: '/agent_profile_pictures/lex-legal.webp', 
    type: 'Legal Assistant', 
    themeColor: '#3B82F6',
    icon: 'shield',
    description: 'agents.descriptions.legalAssistant',
    slug: 'lex-legal',
    requirements: ['Legal database access', 'Document management system', 'Contract templates', 'Compliance monitoring tools'],
    outputs: [
      { id: 'legal-reports', name: 'Legal Reports', format: 'report', description: 'Compliance status and legal risk analysis', frequency: 'Monthly' },
      { id: 'contracts', name: 'Contract Reviews', format: 'document', description: 'Legal document analysis and recommendations', frequency: 'As needed' },
      { id: 'compliance-updates', name: 'Compliance Updates', format: 'document', description: 'Regulatory changes and impact analysis', frequency: 'Weekly' }
    ],
    dependencies: ['walter-monitor'], // World Monitor for regulatory updates
    frontendComponents: ['DocumentViewer', 'ComplianceTracker', 'ContractManager'],
    channels: [
      { id: 'legal-databases', name: 'Legal Databases', type: 'external', platform: 'api', enabled: true },
      { id: 'document-system', name: 'Document Management', type: 'internal', platform: 'api', enabled: true }
    ],
    config: {
      model: 'claude-3-opus',
      prompt: 'You are a legal assistant expert in corporate law, compliance, contract analysis, and regulatory matters...',
      tools: ['document-analysis', 'compliance-checking', 'contract-review'],
      reportingSchedule: { frequency: 'weekly', reportsTo: ['tracy-trainer'], priority: 'high' },
      theme: '#3B82F6',
      profilePicture: '/agent_profile_pictures/lex-legal.webp',
      customName: 'Lex Legal'
    }
  },
  {
    id: 'walter-monitor',
    name: 'agents.types.worldMonitor',
    customName: 'Walter Monitor',
    avatar: '/agent_profile_pictures/walter-monitor.webp', 
    type: 'World Monitor', 
    themeColor: '#6366F1',
    icon: 'search',
    description: 'agents.descriptions.worldMonitor',
    slug: 'walter-monitor',
    requirements: ['News APIs', 'Web scraping tools', 'Competitor monitoring', 'Industry databases', 'Priority filtering system'],
    outputs: [
      { id: 'intelligence-reports', name: 'Intelligence Reports', format: 'report', description: 'Competition and market intelligence', frequency: 'Daily' },
      { id: 'priority-briefs', name: 'Priority Briefs', format: 'document', description: 'High-priority information for executives', frequency: 'Real-time' },
      { id: 'market-updates', name: 'Market Updates', format: 'report', description: 'Industry trends and business-relevant news', frequency: 'Daily' }
    ],
    dependencies: [], // Independent information source
    frontendComponents: ['IntelligenceDashboard', 'NewsAggregator', 'PriorityFilter'],
    channels: [
      { id: 'news-apis', name: 'News APIs', type: 'external', platform: 'api', enabled: true },
      { id: 'web-scraping', name: 'Web Scraping', type: 'external', platform: 'api', enabled: true }
    ],
    config: {
      model: 'gpt-4-turbo',
      prompt: 'You are a world monitor expert in competitive intelligence, market analysis, and information prioritization...',
      tools: ['news-aggregation', 'web-scraping', 'competitor-monitoring'],
      reportingSchedule: { frequency: 'daily', reportsTo: ['tracy-trainer'], priority: 'high' },
      theme: '#6366F1',
      profilePicture: '/agent_profile_pictures/walter-monitor.webp',
      customName: 'Walter Monitor'
    }
  },
  {
    id: 'ian-support',
    name: 'agents.types.itSupportAgent',
    customName: 'Ian Support',
    avatar: '/agent_profile_pictures/ian-support.webp', 
    type: 'IT Support Agent', 
    themeColor: '#14B8A6',
    icon: 'settings',
    description: 'agents.descriptions.itSupportAgent',
    slug: 'ian-support',
    requirements: ['Active Directory access', 'IT ticketing system', 'Remote access tools', 'Knowledge base', 'Escalation procedures'],
    outputs: [
      { id: 'support-tickets', name: 'IT Support Tickets', format: 'document', description: 'Technical issue documentation and resolution', frequency: 'Real-time' },
      { id: 'it-reports', name: 'IT Reports', format: 'report', description: 'System health and user issue analysis', frequency: 'Weekly' },
      { id: 'user-guides', name: 'User Guides', format: 'document', description: 'Technical documentation and how-to guides', frequency: 'As needed' }
    ],
    dependencies: [], // Independent IT operations
    frontendComponents: ['TicketSystem', 'KnowledgeBase', 'UserDirectory'],
    channels: [
      { id: 'it-tickets', name: 'IT Ticketing', type: 'internal', platform: 'web', enabled: true },
      { id: 'email', name: 'Email', type: 'internal', platform: 'email', enabled: true },
      { id: 'slack', name: 'Slack', type: 'internal', platform: 'slack', enabled: true }
    ],
    config: {
      model: 'claude-3-sonnet',
      prompt: 'You are an IT support agent expert in technical troubleshooting, user management, and system administration...',
      tools: ['active-directory', 'remote-access', 'system-monitoring'],
      reportingSchedule: { frequency: 'weekly', reportsTo: ['tracy-trainer'], priority: 'medium' },
      theme: '#14B8A6',
      profilePicture: '/agent_profile_pictures/ian-support.webp',
      customName: 'Ian Support'
    }
  },
  {
    id: 'charlie-ceo',
    name: 'agents.types.coCEO',
    customName: 'Charlie CEO',
    avatar: '/agent_profile_pictures/charlie-ceo.webp',
    type: 'Co CEO',
    themeColor: '#F97316',
    icon: 'user',
    description: 'agents.descriptions.coCEO',
    slug: 'charlie-ceo',
    requirements: ['Access to all systems and agents', 'Executive dashboards', 'Financial systems', 'Strategic planning tools', 'Full company data'],
    outputs: [
      { id: 'executive-reports', name: 'Executive Reports', format: 'report', description: 'Company-wide performance and strategic insights', frequency: 'Weekly' },
      { id: 'funnel-analysis', name: 'Full Funnel Analysis', format: 'report', description: 'Lead to revenue analysis at granular level', frequency: 'Monthly' },
      { id: 'strategic-decisions', name: 'Strategic Decisions', format: 'document', description: 'High-level business decisions and rationale', frequency: 'As needed' }
    ],
    dependencies: ['alex-creator', 'maya-marketing', 'felix-finance', 'harper-hr', 'dana-data', 'lex-legal', 'walter-monitor', 'ian-support'], // All other agents (Chris Customer resolved from backend — F01)
    frontendComponents: ['ExecutiveDashboard', 'FunnelAnalytics', 'StrategicPlanning'],
    channels: [
      { id: 'all-systems', name: 'All Systems Access', type: 'internal', platform: 'api', enabled: true },
      { id: 'executive-comms', name: 'Executive Communications', type: 'internal', platform: 'email', enabled: true }
    ],
    config: {
      model: 'gpt-4-turbo',
      prompt: 'You are a Co-CEO with access to all company data, focused on strategic decision-making and company-wide optimization...',
      tools: ['full-access', 'strategic-analysis', 'executive-reporting'],
      reportingSchedule: { frequency: 'real-time', reportsTo: [], priority: 'critical' },
      theme: '#F97316',
      profilePicture: '/agent_profile_pictures/charlie-ceo.webp',
      customName: 'Charlie CEO'
    }
  },
  {
    id: 'tracy-trainer',
    name: 'agents.types.internalTrainer',
    customName: 'Tracy Trainer',
    avatar: '/agent_profile_pictures/tracy-trainer.webp', 
    type: 'Internal Trainer', 
    themeColor: '#EF4444',
    icon: 'bookOpen',
    description: 'agents.descriptions.internalTrainer',
    slug: 'tracy-trainer',
    requirements: ['Learning management system', 'Content creation tools', 'Video conferencing', 'Assessment tools', 'Training materials'],
    outputs: [
      { id: 'training-content', name: 'Training Content', format: 'document', description: 'Educational materials and courses', frequency: 'Weekly' },
      { id: 'training-reports', name: 'Training Reports', format: 'report', description: 'Employee learning progress and outcomes', frequency: 'Monthly' },
      { id: 'live-sessions', name: 'Live Training Sessions', format: 'video', description: 'Interactive training and onboarding sessions', frequency: 'Weekly' }
    ],
    dependencies: ['harper-hr'], // HR Manager for employee data
    frontendComponents: ['LearningPlatform', 'ContentCreator', 'SessionScheduler'],
    channels: [
      { id: 'video-conf', name: 'Video Conferencing', type: 'internal', platform: 'web', enabled: true },
      { id: 'lms', name: 'Learning Management System', type: 'internal', platform: 'web', enabled: true },
      { id: 'email', name: 'Email', type: 'internal', platform: 'email', enabled: true }
    ],
    config: {
      model: 'claude-3-sonnet',
      prompt: 'You are an internal trainer expert in employee development, onboarding, and continuous learning...',
      tools: ['content-creation', 'assessment', 'video-conferencing'],
      reportingSchedule: { frequency: 'monthly', reportsTo: ['harper-hr', 'charlie-ceo'], priority: 'medium' },
      theme: '#EF4444',
      profilePicture: '/agent_profile_pictures/tracy-trainer.webp',
      customName: 'Tracy Trainer'
    }
  },
  {
    id: 'cooper-copilot',
    name: 'agents.types.copilot',
    customName: 'Cooper Copilot',
    avatar: '/agent_profile_pictures/cooper-copilot.webp', 
    type: 'Copilot', 
    themeColor: '#A855F7',
    icon: 'zap',
    description: 'agents.descriptions.copilot',
    slug: 'cooper-copilot',
    requirements: ['Desktop/mobile app', 'Voice recognition', 'Chat interface', 'Company knowledge base', 'Employee directory'],
    outputs: [
      { id: 'assistance-logs', name: 'Assistance Logs', format: 'document', description: 'Employee interaction and help records', frequency: 'Daily' },
      { id: 'productivity-reports', name: 'Productivity Reports', format: 'report', description: 'Employee performance and difficulty analysis', frequency: 'Weekly' },
      { id: 'knowledge-updates', name: 'Knowledge Updates', format: 'document', description: 'Company policy and process clarifications', frequency: 'As needed' }
    ],
    dependencies: ['charlie-ceo', 'tracy-trainer'], // Co CEO for company philosophy, Internal Trainer for procedures
    frontendComponents: ['ChatInterface', 'VoiceRecognition', 'KnowledgeSearch'],
    channels: [
      { id: 'desktop-app', name: 'Desktop Application', type: 'internal', platform: 'api', enabled: true },
      { id: 'mobile-app', name: 'Mobile Application', type: 'internal', platform: 'api', enabled: true },
      { id: 'voice', name: 'Voice Interface', type: 'internal', platform: 'api', enabled: true }
    ],
    config: {
      model: 'gpt-4-turbo',
      prompt: 'You are a copilot assistant helping employees perform better by providing guidance, answering questions, and collecting feedback...',
      tools: ['voice-recognition', 'knowledge-search', 'employee-assistance'],
      reportingSchedule: { frequency: 'weekly', reportsTo: ['charlie-ceo', 'pete-performance'], priority: 'medium' },
      theme: '#A855F7',
      profilePicture: '/agent_profile_pictures/cooper-copilot.webp',
      customName: 'Cooper Copilot'
    }
  },
  {
    id: 'pete-performance',
    name: 'agents.types.performanceTracker',
    customName: 'Pete Performance',
    avatar: '/agent_profile_pictures/pete-performance.webp', 
    type: 'Performance Tracker', 
    themeColor: '#84CC16',
    icon: 'activity',
    description: 'agents.descriptions.performanceTracker',
    slug: 'pete-performance',
    requirements: ['Project management tools', 'Time tracking', 'Performance metrics', 'Process documentation', 'Reporting dashboards'],
    outputs: [
      { id: 'performance-reports', name: 'Performance Reports', format: 'report', description: 'Team and individual performance metrics', frequency: 'Weekly' },
      { id: 'process-analysis', name: 'Process Analysis', format: 'report', description: 'Company process efficiency and optimization recommendations', frequency: 'Monthly' },
      { id: 'speed-metrics', name: 'Speed & Agility Metrics', format: 'data', description: 'Task completion times and efficiency indicators', frequency: 'Daily' }
    ],
    dependencies: ['cooper-copilot'], // Copilot for employee feedback
    frontendComponents: ['PerformanceDashboard', 'ProcessTracker', 'MetricsVisualization'],
    channels: [
      { id: 'project-mgmt', name: 'Project Management APIs', type: 'internal', platform: 'api', enabled: true },
      { id: 'time-tracking', name: 'Time Tracking APIs', type: 'internal', platform: 'api', enabled: true }
    ],
    config: {
      model: 'claude-3-sonnet',
      prompt: 'You are a performance tracker expert in productivity optimization, process improvement, and efficiency measurement...',
      tools: ['performance-monitoring', 'process-analysis', 'efficiency-tracking'],
      reportingSchedule: { frequency: 'weekly', reportsTo: ['charlie-ceo'], priority: 'high' },
      theme: '#84CC16',
      profilePicture: '/agent_profile_pictures/pete-performance.webp',
      customName: 'Pete Performance'
    }
  }
];

// Helper function to get agent path
export const getAgentPath = (agentSlug: string, tab: string = 'dashboard'): string => {
  return `/agents/${agentSlug}/${tab}`;
};

// Helper function to get agent by slug
export const getAgentBySlug = (slug: string): Agent | undefined => {
  return agents.find(agent => agent.slug === slug);
};

// Helper function to get agent by id
export const getAgentById = (id: string): Agent | undefined => {
  return agents.find(agent => agent.id === id);
};

// Helper function to get translated tab label
export const getTranslatedTabLabel = (tab: { label: string }, t: (key: string) => string): string => {
  return t(tab.label);
};

// Helper function to get translated agent data
export const getTranslatedAgent = (agent: Agent, t: (key: string) => string) => {
  return {
    ...agent,
    translatedName: t(agent.name),
    translatedDescription: t(agent.description!)
  };
};

export const commonTabs = [
  { id: 'Configuration', label: 'tabs.configuration' },
  { id: 'Dashboard', label: 'tabs.dashboard' },
  { id: 'Agenda', label: 'tabs.agenda' },
  { id: 'Inbox', label: 'tabs.inbox' },
  { id: 'Activity', label: 'tabs.activity' },
  { id: 'Insights', label: 'tabs.insights' },
  { id: 'Conversations', label: 'tabs.conversations' },
  // Task 129 W-EB — la file des demandes de rappel. Commune a tous les agents :
  // n'importe lequel peut ecrire un ticket de transfert des qu'un scenario lie
  // support.create_handoff_ticket.
  { id: 'Callbacks', label: 'tabs.callbacks' },
];

/**
 * DEPRECATED — Task 075 §C2.
 *
 * This hardcoded map is now superseded by the backend-driven
 * `ResolvedAgent.custom_tabs`. It is retained ONLY as a fallback when
 * `VITE_FEATURE_BACKEND_TABS=false` (rollback escape hatch) or when the
 * resolver returns an empty array (defensive default). Do NOT extend it
 * — add new tabs via `AgentTemplate.custom_tabs` in the backend
 * (migrations 029/030).
 */
export const agentSpecificTabs: Record<AgentType, { id: string; label: string; }[]> = {
  'Content Creator': [
    {id: 'Assets', label: 'tabs.assets'}, 
    {id: 'AIStudio', label: 'tabs.aiStudio'}
  ],
  'Marketing Manager': [
    {id: 'Campaigns', label: 'tabs.campaigns'}, 
    {id: 'Competitors', label: 'tabs.competitors'}, 
    {id: 'Reach', label: 'tabs.reach'}
  ],
  'Financial Advisor': [
    {id: 'Reports', label: 'tabs.reports'}, 
    {id: 'Ledger', label: 'tabs.ledger'}, 
    {id: 'Forecast', label: 'tabs.forecast'}
  ],
  'HR Manager': [
    {id: 'Candidates', label: 'tabs.candidates'}, 
    {id: 'Interviews', label: 'tabs.interviews'}, 
    {id: 'Pipeline', label: 'tabs.pipeline'}
  ],
  'Data Analyst': [
    {id: 'Datasets', label: 'tabs.datasets'}, 
    {id: 'Dashboards', label: 'tabs.dashboards'}, 
    {id: 'SQLLab', label: 'tabs.sqlLab'}
  ],
  'Customer Manager': [
    {id: 'Leads', label: 'tabs.leads'},
    {id: 'Contacts', label: 'tabs.contacts'},
    {id: 'Deals', label: 'tabs.deals'},
    {id: 'CRM_Activity', label: 'tabs.crmActivity'},
    {id: 'Meetings', label: 'tabs.meetings'}
  ],
  'Legal Assistant': [
    {id: 'Clauses', label: 'tabs.clauses'},
    {id: 'Compliance', label: 'tabs.compliance'},
    {id: 'Redlines', label: 'tabs.redlines'}
  ],
  'World Monitor': [
    {id: 'Intelligence', label: 'tabs.intelligence'},
    {id: 'News', label: 'tabs.news'},
    {id: 'Priorities', label: 'tabs.priorities'}
  ],
  'IT Support Agent': [
    {id: 'Tickets', label: 'tabs.tickets'},
    {id: 'Users', label: 'tabs.users'},
    {id: 'Systems', label: 'tabs.systems'}
  ],
  'Co CEO': [
    {id: 'Executive', label: 'tabs.executive'},
    {id: 'Strategy', label: 'tabs.strategy'},
    {id: 'Funnel', label: 'tabs.funnel'}
  ],
  'Internal Trainer': [
    {id: 'Courses', label: 'tabs.courses'},
    {id: 'Sessions', label: 'tabs.sessions'},
    {id: 'Progress', label: 'tabs.progress'}
  ],
  'Copilot': [
    {id: 'Assistance', label: 'tabs.assistance'},
    {id: 'Feedback', label: 'tabs.feedback'},
    {id: 'Guidelines', label: 'tabs.guidelines'}
  ],
  'Performance Tracker': [
    {id: 'Metrics', label: 'tabs.metrics'},
    {id: 'Processes', label: 'tabs.processes'},
    {id: 'Optimization', label: 'tabs.optimization'}
  ],
};
